import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import {
  MultiLineChartComponent,
  MultiLineChartDataPoint,
  MultiLineChartSeries,
  LegendItem
} from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ModalService } from '../../../../../_shared/services/modals/modal.service';
import { DashboardService } from '../../../services/dashboard.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { WorkOrdersModalComponent } from '../../modals/work-orders-modal/work-orders-modal.component';
import { MaintenanceInsightsResponse } from '../../../../maintenance/maintenance-dashboard/models/maintenance-dashboard-response.model';
import { differenceInDays } from 'date-fns';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { SummaryRequest } from '../../../../../_shared/models';
import { transformValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-work-orders-trend',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, MultiLineChartComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './work-orders-trend.component.html',
  styleUrl: './work-orders-trend.component.scss'
})
export class WorkOrdersTrendComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(ModalService);

  chartData: MultiLineChartDataPoint[] = [];
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly chartHeight = 145;

  // The chart component renders these strings itself, so they are rebuilt on every language change.
  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];

  title: string = 'maintenanceDashboard.charts.workOrders';
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter!: WidgetFilterData;

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    // langChanges$ replays the active language, so this also does the initial build
    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChartLabels());

    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadWorkOrdersData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadWorkOrdersData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private buildChartLabels(): void {
    const completedWorkOrders = this.transloco.translate('maintenanceDashboard.charts.completedWorkOrders');
    const globalTrend = this.transloco.translate('maintenanceDashboard.charts.globalTrend');

    this.series = [
      { key: 'globalTrend', color: '#EF4444', label: globalTrend, showDots: false },
      { key: 'completedWorkOrders', color: '#3B82F6', label: completedWorkOrders, showDots: true }
    ];

    this.legendItems = [
      { legend: completedWorkOrders, color: '#3B82F6' },
      { legend: globalTrend, color: '#EF4444' }
    ];
  }

  private async loadWorkOrdersData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const summaryRequest: SummaryRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        accountIds: this.widgetFilter.accountIds
      };

      const [completedResponse, globalResponse, statsResponse] = await Promise.all([
        this.dashboardService.getCompletedWorkOrdersRatio(request, this.cancelSubject),
        this.dashboardService.getGlobalCompletedWorkOrdersRatio(request, this.cancelSubject),
        this.dashboardService.getCompletedWorkOrdersStats(summaryRequest, this.cancelSubject)
      ]);

      this.chartData = this.processWorkOrdersData(
        completedResponse.data ?? [],
        globalResponse.data ?? []
      );

      this.totalValue = statsResponse?.data?.totalValue ?? 0;
      this.percentageChange = statsResponse?.data?.percentageChange ?? 0;

      this.calculateYTicks(this.chartData);
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.totalValue = 0;
          this.percentageChange = 0;
          this.isLoading = false;
        }
      });
    }
  }

  private processWorkOrdersData(
    completedData: MaintenanceInsightsResponse[],
    globalData: MaintenanceInsightsResponse[]
  ): MultiLineChartDataPoint[] {
    if (completedData.length === 0 && globalData.length === 0) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      {
        label: 'completedWorkOrders',
        data: completedData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      },
      {
        label: 'globalTrend',
        data: globalData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      }
    ];

    let groupedData: any[];

    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    return groupedData.map(item => ({
      x: item.label,
      completedWorkOrders: Number(item['completedWorkOrders']) || 0,
      globalTrend: Number(item['globalTrend']) || 0
    }));
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues: number[] = [];

    data.forEach(point => {
      this.series.forEach(s => {
        const value = point[s.key];
        if (typeof value === 'number') {
          allValues.push(value);
        }
      });
    });

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true; // Work orders increase is generally positive
  }

  formatValue(): string {
    return transformValue(this.totalValue);
  }

  formatTooltipValue(value: number): string {
    return `${value.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    this.modalService.open(WorkOrdersModalComponent, {
      size: 'xl',
      centered: true,
      windowClass: 'work-orders-modal'
    });
  }
}